import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, GraduationCap, Calendar, ChevronsUp } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useApiErrors, useEvent, useLazyCommand } from '@soap/modules';
import { TreeNode, TreeNodeRenderer } from './shared/TreeNode';
import { useTreeSelection } from './shared/useTreeSelection';
import { useTreeExpansion } from './shared/useTreeExpansion';
import { useTopicState } from './shared/useTopicState';
import { sortYears } from './shared/sortYears';
import { Link } from 'react-router';
import { Label } from '../../../ui/label';
import { studentCurriculumYearMayHaveTopics } from '../utils/topicUtils';
import {
    applyReorderedYearTopics,
    parseStudentCurriculumYearFromNodeId,
    studentCurriculumYearNodeId,
    studentSubjectBranchIdFromE180
} from '../utils/curriculumUtils';
import {
    groupStudentTopicsByPrimaryParent,
    hasRealChildTopics,
    parseStudentCurriculumSegmentDroppableId,
    reorderSegmentsInYearTopics,
    reorderTopicsWithinSegment,
    type YearTopicSegment
} from '../utils/groupStudentTopicsByPrimaryParent';
import { getTopicInstructionalRollup, type InstructionalRollupKind } from '../utils/topicInstructionalUnits';

interface StudentCurriculumSubject {
  e180_Name: string;
  e180_SubjectId: string;
  e180_KeyStages: StudentCurriculumKeyStage[];
}

interface StudentCurriculumKeyStage {
  e180_Name?: string;
  e180_KeyStageId: string;
  e180_Years: StudentCurriculumYear[];
}

interface StudentCurriculumYear {
  e180_YearId?: string;
  e180_YearLabel?: string;
  e180_Topics: StudentCurriculumTopic[];
  e180_TotalTopicCount?: number | null;
}

interface StudentCurriculumTopic {
  e180_Name?: string;
  e180_TopicId: string;
  e180_Details: string;
  e180_ChildTopics: StudentCurriculumTopic[];
  e180_MinHours?: number;
  e180_PriorKnowledgeRequired?: string[];
  e180_NationalCurriculumContent?: string[];
  e180_Keywords?: Array<{ e180_Keyword: string; e180_Description?: string }>;
  e180_LearningObjectives?: string[];
  e180_SupervisionLevel?: string;
  e180_Outcome?: string;
  e180_TeacherTip?: string;
  e180_Misconceptions?: string;
  e180_ProviderKey?: string;
  e180_ProviderTopicId?: string;
  e180_State?: any;
  e180_ParentTopicNames?: string[];
  e180_TotalChildTopicCount?: number | null;
}

interface StudentCurriculumStructure {
  e180_Subjects: StudentCurriculumSubject[];
}

interface StudentCurriculumTreeViewProps {
  onNodeSelect?: (node: TreeNode) => void;
  selectedTopics?: any[];
  className?: string;
  expandedNodes?: Set<string>;
  onExpandedNodesChange?: (nodes: Set<string>) => void;
  studentCurriculumData?: StudentCurriculumStructure | null;
  isStudentCurriculumSelecting?: boolean;
  units?: any[];
  passFailThreshold?: number | null;
  onLoadNodeChildren?: (nodeId: string, nodeType: 'year' | 'topic') => void;
  loadedNodeIds?: Set<string>;
  loadingNodeIds?: Set<string>;
  studentId?: string | null;
  selectedStudentFirstName?: string;
  /** Open the matching unit on Review / In progress / Completed (Create tab routing). */
  onOpenInstructionalUnit?: (unit: any) => void;
  /** Apply optimistic patches to the student curriculum tree (e.g. reorder). */
  setStudentCurriculumData?: (data: any) => void;
}

const viewStates = {
  loading: 0,
  error: 1,
  empty: 2,
  noData: 3,
  ready: 4
};

export function StudentCurriculumTreeView({
  onNodeSelect,
  selectedTopics = [],
  className = '',
  expandedNodes,
  onExpandedNodesChange,
  studentCurriculumData,
  isStudentCurriculumSelecting = false,
  units = [],
  passFailThreshold = null,
  onLoadNodeChildren,
  loadedNodeIds = new Set(),
  loadingNodeIds = new Set(),
  studentId = null,
  selectedStudentFirstName = '',
  onOpenInstructionalUnit,
  setStudentCurriculumData
}: StudentCurriculumTreeViewProps) {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [viewState, setViewState] = useState<number>(!studentCurriculumData ? viewStates.loading : viewStates.ready);
  const [nodesToAutoSelect, setNodesToAutoSelect] = useState<Set<string>>(new Set());

  const studentFirstName = selectedStudentFirstName?.trim() || 'Student';
  const studentCurriculumLabel = `${studentFirstName}'s Curriculum`;
  const collapseAllTitle = 'Collapse All';

  // Update viewState based on studentCurriculumData
  useEffect(() => {
    if (!studentCurriculumData) {
      setViewState(viewStates.loading);
    } else if (!studentCurriculumData.e180_Subjects || studentCurriculumData.e180_Subjects.length === 0) {
      setViewState(viewStates.empty);
    } else {
      setViewState(viewStates.ready);
    }
  }, [studentCurriculumData]);

  // Topics in the student curriculum are never disabled (can be selected for removal/lesson creation)
  const isTopicDisabled = (): boolean => false;

  // Use shared hooks
  const { isTopicSelected, getLeafTopicNodes, getCheckboxState, hasDisabledDescendants } = useTreeSelection({
    selectedTopics,
    isSelecting: isStudentCurriculumSelecting,
    isTopicDisabled
  });

  const { toggleNode, handleCollapseAll, nodesToAutoSelect: autoSelectNodes, setNodesToAutoSelect: setAutoSelectNodes } = useTreeExpansion({
    expandedNodes,
    onExpandedNodesChange,
    onLoadNodeChildren,
    loadedNodeIds,
    loadingNodeIds,
    canLoadChildren: true // Student curriculum always supports lazy loading
  });

  const { getAssessmentScoreForTopic } = useTopicState({
    units,
    passFailThreshold,
    studentCurriculumData,
    isStudentCurriculum: true
  });

  const [sendReorderTopicsCommand] = useLazyCommand('C288v1_ReorderTopicsInStudentCurriculum');
  const pendingReorderSnapshotRef = useRef<any | null>(null);

  useApiErrors(() => {
    if (pendingReorderSnapshotRef.current != null && setStudentCurriculumData) {
      setStudentCurriculumData(pendingReorderSnapshotRef.current);
      pendingReorderSnapshotRef.current = null;
    }
  });

  useEvent({
    eventName: 'E274v1_GotReorderedTopics',
    onEventReceived: () => {
      pendingReorderSnapshotRef.current = null;
    }
  });

  const sendReorderCommand = (
    yearNodeId: string,
    subject: StudentCurriculumSubject,
    keyStage: StudentCurriculumKeyStage,
    year: StudentCurriculumYear,
    reorderedTopics: StudentCurriculumTopic[]
  ) => {
    const orderedTopicIds = reorderedTopics
      .map(topic => topic.e180_TopicId)
      .filter((id): id is string => !!id);

    if (orderedTopicIds.length === 0) return;

    if (setStudentCurriculumData) {
      pendingReorderSnapshotRef.current = studentCurriculumData;
      const optimistic = applyReorderedYearTopics(studentCurriculumData, yearNodeId, reorderedTopics);
      if (optimistic) {
        setStudentCurriculumData(optimistic);
      }
    }

    const studentSubjectEntityId = subject.e180_SubjectId;
    if (!studentSubjectEntityId?.trim()) {
      console.error('Reorder requires e180_SubjectId on the subject row.');
      return;
    }

    const ksId = keyStage.e180_KeyStageId != null ? String(keyStage.e180_KeyStageId).trim() : '';
    const yId = year.e180_YearId != null ? String(year.e180_YearId).trim() : '';
    if (!ksId || !yId) {
      console.error('Reorder requires e180_KeyStageId and e180_YearId on the year row.');
      return;
    }

    void sendReorderTopicsCommand({
      C288_StudentId: studentId,
      C288_StudentSubjectId: studentSubjectEntityId,
      C288_KeyStageId: ksId,
      C288_YearId: yId,
      C288_OrderedTopicIds: orderedTopicIds
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.droppableId !== result.destination.droppableId) {
      return;
    }

    if (result.source.index === result.destination.index) {
      return;
    }

    if (!studentId || !studentCurriculumData?.e180_Subjects) return;

    const dragType = result.type;

    if (dragType === 'SEGMENT') {
      const yearNodeId = result.source.droppableId;
      const parsed = parseStudentCurriculumYearFromNodeId(studentCurriculumData.e180_Subjects, yearNodeId);
      if (!parsed) return;

      const { subject, keystageIndex, yearIndex } = parsed;
      const keyStage = subject.e180_KeyStages?.[keystageIndex];
      const year = keyStage?.e180_Years?.[yearIndex];
      if (!year?.e180_Topics?.length) return;

      const segments = groupStudentTopicsByPrimaryParent(year.e180_Topics);
      const reorderedTopics = reorderSegmentsInYearTopics(
        year.e180_Topics,
        segments,
        result.source.index,
        result.destination.index
      );
      sendReorderCommand(yearNodeId, subject, keyStage, year, reorderedTopics);
      return;
    }

    if (dragType === 'TOPIC') {
      const segmentParsed = parseStudentCurriculumSegmentDroppableId(result.source.droppableId);
      if (!segmentParsed) return;

      const { yearNodeId, runIndex } = segmentParsed;
      const parsed = parseStudentCurriculumYearFromNodeId(studentCurriculumData.e180_Subjects, yearNodeId);
      if (!parsed) return;

      const { subject, keystageIndex, yearIndex } = parsed;
      const keyStage = subject.e180_KeyStages?.[keystageIndex];
      const year = keyStage?.e180_Years?.[yearIndex];
      if (!year?.e180_Topics?.length) return;

      const segments = groupStudentTopicsByPrimaryParent(year.e180_Topics);
      const segment = segments[runIndex];
      if (!segment || segment.topics.length <= 1) return;

      const reorderedTopics = reorderTopicsWithinSegment(
        year.e180_Topics,
        segment,
        result.source.index,
        result.destination.index
      );
      sendReorderCommand(yearNodeId, subject, keyStage, year, reorderedTopics);
    }
  };

  // Auto-select children when they become available
  useEffect(() => {
    if (!isStudentCurriculumSelecting || autoSelectNodes.size === 0) return;
    
    const treeNodes = buildTreeNodes();
    const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    const nodesToProcess = Array.from(autoSelectNodes);
    nodesToProcess.forEach(nodeId => {
      if (loadedNodeIds.has(nodeId) && !loadingNodeIds.has(nodeId)) {
        const node = findNodeById(treeNodes, nodeId);
        if (node) {
          const leafNodes = getLeafTopicNodes(node);
          const selectableLeafTopics = leafNodes
            .filter(leafNode => leafNode.data)
            .map(leafNode => leafNode.data);
          
          selectableLeafTopics.forEach(topicData => {
            if (topicData && !isTopicSelected(topicData)) {
              const topicNode: TreeNode = {
                id: `${nodeId}-leaf-${topicData.e180_TopicId}`,
                label: topicData.e180_Name || '',
                type: 'topic',
                data: topicData
              };
              onNodeSelect?.(topicNode);
            }
          });
          
          setAutoSelectNodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(nodeId);
            return newSet;
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedNodeIds, loadingNodeIds, studentCurriculumData]);

  const selectNode = (node: TreeNode) => {
    setSelectedNode(node.id);
    onNodeSelect?.(node);
  };

  const handleCheckboxClick = (node: TreeNode, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isStudentCurriculumSelecting) return;

    if (node.type === 'topicGroup') {
      const isExpanded = expandedNodes ? expandedNodes.has(node.id) : false;
      const currentState = getCheckboxState(node);

      if (!isExpanded && currentState === 'unchecked') {
        toggleNode(node.id, 'topicGroup');
        setAutoSelectNodes(prev => new Set(prev).add(node.id));
        return;
      }

      const leafNodes = getLeafTopicNodes(node);
      const shouldSelect = currentState !== 'checked';
      const selectableLeafTopics = leafNodes
        .filter(leafNode => leafNode.data)
        .map(leafNode => leafNode.data);

      if (shouldSelect) {
        selectableLeafTopics.forEach(topicData => {
          if (topicData && !isTopicSelected(topicData)) {
            const topicNode: TreeNode = {
              id: `${node.id}-leaf-${topicData.e180_TopicId}`,
              label: topicData.e180_Name || '',
              type: 'topic',
              data: topicData
            };
            onNodeSelect?.(topicNode);
          }
        });
      } else {
        selectableLeafTopics.forEach(topicData => {
          if (topicData && isTopicSelected(topicData)) {
            const topicNode: TreeNode = {
              id: `${node.id}-leaf-${topicData.e180_TopicId}`,
              label: topicData.e180_Name || '',
              type: 'topic',
              data: topicData
            };
            onNodeSelect?.(topicNode);
          }
        });
      }
      return;
    }
    
    // For student curriculum, topics are always leaf nodes (no nested topics)
    // If it's a leaf topic (no child topics in data), select just this one
    if (node.type === 'topic' && node.data) {
      const hasChildTopics = node.data.e180_ChildTopics && node.data.e180_ChildTopics.length > 0;
      if (!hasChildTopics) {
        // Toggle selection: if selected, deselect; if not selected, select
        onNodeSelect?.(node);
        return;
      }
    }
    
    const isExpanded = expandedNodes ? expandedNodes.has(node.id) : false;
    const currentState = getCheckboxState(node);
    
    if (!isExpanded && currentState === 'unchecked') {
      if (onLoadNodeChildren && !loadedNodeIds.has(node.id) && !loadingNodeIds.has(node.id)) {
        const mayLazyYear = node.type === 'year' && node.data && studentCurriculumYearMayHaveTopics(node.data);
        if (mayLazyYear) {
          toggleNode(node.id, 'year');
          setAutoSelectNodes(prev => new Set(prev).add(node.id));
          return;
        }
      }
    }
    
    const leafNodes = getLeafTopicNodes(node);
    const shouldSelect = currentState !== 'checked';
    const selectableLeafTopics = leafNodes
      .filter(leafNode => leafNode.data)
      .map(leafNode => leafNode.data);
    
    if (shouldSelect) {
      selectableLeafTopics.forEach(topicData => {
        if (topicData && !isTopicSelected(topicData)) {
          const topicNode: TreeNode = {
            id: `${node.id}-leaf-${topicData.e180_TopicId}`,
            label: topicData.e180_Name || '',
            type: 'topic',
            data: topicData
          };
          onNodeSelect?.(topicNode);
        }
      });
    } else {
      selectableLeafTopics.forEach(topicData => {
        if (topicData && isTopicSelected(topicData)) {
          const topicNode: TreeNode = {
            id: `${node.id}-leaf-${topicData.e180_TopicId}`,
            label: topicData.e180_Name || '',
            type: 'topic',
            data: topicData
          };
          onNodeSelect?.(topicNode);
        }
      });
    }
  };

  const buildTreeNodes = (): TreeNode[] => {
    if (!studentCurriculumData || !studentCurriculumData.e180_Subjects) return [];

    // Sort subjects alphanumerically
    const sortedSubjects = [...studentCurriculumData.e180_Subjects].sort((a, b) => 
      (a.e180_Name || '').localeCompare(b.e180_Name || '')
    );

    return sortedSubjects.map(subject => {
      const branchId = studentSubjectBranchIdFromE180(subject);
      const studentSubjectId =
        subject.e180_SubjectId != null && String(subject.e180_SubjectId).trim() !== ''
          ? String(subject.e180_SubjectId)
          : null;
      const subjectDisplayName = subject.e180_Name || '';
      const subjectEs =
        (subject as { e180_EducationSystemKey?: string }).e180_EducationSystemKey?.trim() || null;
      // Sort key stages alphanumerically
      const sortedKeyStages = [...subject.e180_KeyStages].sort((a, b) => 
        (a.e180_Name || '').localeCompare(b.e180_Name || '')
      );
      
        return {
          id: branchId,
          label: subjectDisplayName,
          type: 'subject' as const,
          icon: BookOpen,
          children: sortedKeyStages.map((keystage) => {
            // Find original index for node ID
            const originalKeyStageIndex = subject.e180_KeyStages.indexOf(keystage);
            return {
              id: `${branchId}-ks-${originalKeyStageIndex}`,
              label: keystage.e180_Name || `Key Stage ${originalKeyStageIndex + 1}`,
              type: 'keystage' as const,
              icon: GraduationCap,
              children: sortYears(keystage.e180_Years, true).map((year) => {
                const originalYearIndex = keystage.e180_Years.indexOf(year);
                const yearNodeId = studentCurriculumYearNodeId(
                  subject,
                  originalKeyStageIndex,
                  originalYearIndex
                );
            const topicsLoaded = loadedNodeIds.has(yearNodeId);
            const topics = topicsLoaded && year.e180_Topics ? year.e180_Topics : [];
            const yearLabel = (year.e180_YearLabel || '').trim();
            const yearDisplay = yearLabel || `Year ${originalYearIndex + 1}`;
            const ksId =
              keystage.e180_KeyStageId != null && String(keystage.e180_KeyStageId).trim() !== ''
                ? String(keystage.e180_KeyStageId)
                : null;
            const yId =
              year.e180_YearId != null && String(year.e180_YearId).trim() !== ''
                ? String(year.e180_YearId)
                : null;

            return {
              id: yearNodeId,
              label: yearDisplay,
              type: 'year' as const,
              icon: Calendar,
              data: {
                e180_TotalTopicCount: year.e180_TotalTopicCount
              },
              children: topics.length > 0
                  ? buildGroupedYearChildren(
                    yearNodeId,
                    subjectDisplayName,
                    branchId,
                    studentSubjectId,
                    subjectEs,
                    keystage.e180_Name,
                    ksId,
                    yId,
                    yearDisplay,
                    originalKeyStageIndex,
                    originalYearIndex,
                    topics
                  )
                : undefined
            };
          })
        }
      })
      };
    });
  };

  const buildTopicWithMetadata = (
    topic: any,
    subjectDisplayName: string,
    studentSubjectId: string | null,
    educationSystemKey: string | null,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    insideGroup: boolean
  ) => ({
    ...topic,
    _metadata: {
      subjectName: subjectDisplayName,
      subjectId: studentSubjectId,
      keyStageName,
      keyStageId,
      yearId,
      yearLabel: yearDisplayLabel,
      educationSystemKey
    },
    _insideGroup: insideGroup
  });

  const buildLeafTopicNode = (
    topic: any,
    flatIndex: number,
    subjectDisplayName: string,
    subjectBranchId: string,
    studentSubjectId: string | null,
    educationSystemKey: string | null,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    keystageIndex: number,
    yearIndex: number,
    insideGroup: boolean
  ): TreeNode => {
    const topicWithMetadata = buildTopicWithMetadata(
      topic,
      subjectDisplayName,
      studentSubjectId,
      educationSystemKey,
      keyStageName,
      keyStageId,
      yearId,
      yearDisplayLabel,
      insideGroup
    );
    return {
      id: `${subjectBranchId}-${keystageIndex}-${yearIndex}-${flatIndex}`,
      label: topic.e180_Name || `Topic ${flatIndex + 1}`,
      type: 'topic' as const,
      data: topicWithMetadata
    };
  };

  const buildGroupedYearChildren = (
    yearNodeId: string,
    subjectDisplayName: string,
    subjectBranchId: string,
    studentSubjectId: string | null,
    educationSystemKey: string | null,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    keystageIndex: number,
    yearIndex: number,
    topics: any[]
  ): TreeNode[] => {
    const segments = groupStudentTopicsByPrimaryParent(topics);

    return segments.map((segment: YearTopicSegment, runIndex: number) => {
      if (segment.kind === 'group') {
        return {
          id: `${yearNodeId}-grp-${runIndex}`,
          label: segment.label,
          type: 'topicGroup' as const,
          data: { segment, runIndex },
          children: segment.topics.map((topic, relIndex) =>
            buildLeafTopicNode(
              topic,
              segment.startIndex + relIndex,
              subjectDisplayName,
              subjectBranchId,
              studentSubjectId,
              educationSystemKey,
              keyStageName,
              keyStageId,
              yearId,
              yearDisplayLabel,
              keystageIndex,
              yearIndex,
              true
            )
          )
        };
      }

      const topic = segment.topics[0];
      if (hasRealChildTopics(topic)) {
        return buildTopicNodes(
          subjectDisplayName,
          subjectBranchId,
          studentSubjectId,
          educationSystemKey,
          keyStageName,
          keyStageId,
          yearId,
          yearDisplayLabel,
          keystageIndex,
          yearIndex,
          [topic],
          segment.startIndex
        )[0];
      }

      return buildLeafTopicNode(
        topic,
        segment.startIndex,
        subjectDisplayName,
        subjectBranchId,
        studentSubjectId,
        educationSystemKey,
        keyStageName,
        keyStageId,
        yearId,
        yearDisplayLabel,
        keystageIndex,
        yearIndex,
        false
      );
    });
  };

  const buildTopicNodes = (
    subjectDisplayName: string,
    subjectBranchId: string,
    studentSubjectId: string | null,
    educationSystemKey: string | null,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    keystageIndex: number,
    yearIndex: number,
    topics: any[],
    flatIndexOffset = 0
  ): TreeNode[] => {
    return topics.map((topic, topicIndex) => {
      const flatIndex = flatIndexOffset + topicIndex;
      const topicWithMetadata = buildTopicWithMetadata(
        topic,
        subjectDisplayName,
        studentSubjectId,
        educationSystemKey,
        keyStageName,
        keyStageId,
        yearId,
        yearDisplayLabel,
        false
      );
      return {
        id: `${subjectBranchId}-${keystageIndex}-${yearIndex}-${flatIndex}`,
        label: topic.e180_Name || `Topic ${flatIndex + 1}`,
        type: 'topic' as const,
        data: topicWithMetadata,
        children: topic.e180_ChildTopics && topic.e180_ChildTopics.length > 0 
          ? buildChildTopicNodes(
              subjectDisplayName,
              subjectBranchId,
              studentSubjectId,
              educationSystemKey,
              keyStageName,
              keyStageId,
              yearId,
              yearDisplayLabel,
              keystageIndex,
              yearIndex,
              flatIndex,
              topic.e180_ChildTopics
            )
          : undefined
      };
    });
  };

  const buildChildTopicNodes = (
    subjectDisplayName: string,
    subjectBranchId: string,
    studentSubjectId: string | null,
    educationSystemKey: string | null,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    keystageIndex: number,
    yearIndex: number,
    parentTopicIndex: number | string,
    childTopics: any[]
  ): TreeNode[] => {
    return childTopics.map((childTopic, childIndex) => {
      const childTopicWithMetadata = buildTopicWithMetadata(
        childTopic,
        subjectDisplayName,
        studentSubjectId,
        educationSystemKey,
        keyStageName,
        keyStageId,
        yearId,
        yearDisplayLabel,
        false
      );
      const childTopicNodeId = `${subjectBranchId}-${keystageIndex}-${yearIndex}-${parentTopicIndex}-${childIndex}`;
      const grandChildTopics = childTopic.e180_ChildTopics ?? [];

      return {
        id: childTopicNodeId,
        label: childTopic.e180_Name || `Sub-topic ${childIndex + 1}`,
        type: 'topic' as const,
        data: childTopicWithMetadata,
        children: grandChildTopics.length > 0
          ? buildChildTopicNodes(
              subjectDisplayName,
              subjectBranchId,
              studentSubjectId,
              educationSystemKey,
              keyStageName,
              keyStageId,
              yearId,
              yearDisplayLabel,
              keystageIndex,
              yearIndex,
              `${parentTopicIndex}-${childIndex}`,
              grandChildTopics
            )
          : undefined
      };
    });
  };


  const renderTreeNode = (node: TreeNode, depth: number = 0, dragHandleProps?: any): React.ReactNode => {
    const isExpanded = expandedNodes ? expandedNodes.has(node.id) : false;
    const isSelected = selectedNode === node.id;
    const hasChildren = node.children && node.children.length > 0;
    const isLoading = loadingNodeIds.has(node.id);
    const yearLazyExpand =
      node.type === 'year' &&
      Boolean(node.data) &&
      onLoadNodeChildren &&
      !loadedNodeIds.has(node.id) &&
      studentCurriculumYearMayHaveTopics(node.data);
    const canHaveChildren = yearLazyExpand;
    const showExpandIcon = hasChildren || canHaveChildren;
    
    const isTopicSelectedInCards = node.type === 'topic' && node.data ? isTopicSelected(node.data) : false;
    const isDisabled = false; // Student curriculum topics are never disabled
    const leafTopicWithoutChildren =
      node.type === 'topic' &&
      Boolean(node.data) &&
      (!node.children || node.children.length === 0);

    const topicIdForLeaf = leafTopicWithoutChildren && node.data ? node.data.e180_TopicId : null;

    let instructionalRollup: InstructionalRollupKind | undefined;
    let leafInstructionalUnits: any[] = [];
    if (leafTopicWithoutChildren && topicIdForLeaf) {
      const rolled = getTopicInstructionalRollup(units, topicIdForLeaf);
      leafInstructionalUnits = rolled.qualifyingUnits;
      instructionalRollup = rolled.rollup;
    }

    const assessmentScore =
      leafTopicWithoutChildren && instructionalRollup === 'allComplete' && topicIdForLeaf
        ? getAssessmentScoreForTopic(topicIdForLeaf)
        : null;
    
    const showCheckbox = isStudentCurriculumSelecting && (node.type === 'year' || node.type === 'topic' || node.type === 'topicGroup');
    const checkboxState = showCheckbox ? getCheckboxState(node) : 'unchecked';
    const isChecked = checkboxState === 'checked';
    const isIndeterminate = checkboxState === 'indeterminate';
    const hasDisabledDesc = false; // Student curriculum has no disabled descendants
    const checkboxDisabled = false; // Student curriculum checkboxes are never disabled

    const hasChildTopics = node.type === 'topic' && node.data && node.data.e180_ChildTopics && node.data.e180_ChildTopics.length > 0;
    const topicGroupChildCount = node.type === 'topicGroup' && node.children ? node.children.length : 0;
    const childTopicsCount = node.type === 'topic' && node.data
      ? (node.data.e180_TotalChildTopicCount ?? (node.data.e180_ChildTopics?.length || 0))
      : topicGroupChildCount;
    const yearTopicsCount = node.type === 'year' && node.data ? node.data.e180_TotalTopicCount : undefined;
    const insideGroup = Boolean(node.data?._insideGroup);
    const parentTopicNames = node.type === 'topic' && node.data && !insideGroup
      ? (node.data.e180_ParentTopicNames || [])
      : undefined;

    const handleToggleOrSelect = () => {
      if (isDisabled) return;

      if (node.type === 'topicGroup') {
        toggleNode(node.id, 'topicGroup');
        return;
      }

      const isParentTopic =
        node.type === 'topic' &&
        node.data &&
        node.data.e180_ChildTopics &&
        node.data.e180_ChildTopics.length > 0;

      if (isParentTopic) {
        toggleNode(node.id, node.type);
      } else {
        if (
          hasChildren ||
          (node.type === 'year' && (yearLazyExpand || isExpanded)) ||
          (node.type === 'topic' && (isExpanded || hasChildTopics))
        ) {
          toggleNode(node.id, node.type);
        }
        if (node.type === 'topic' && node.data) {
          selectNode(node);
        }
      }
    };

    const renderTopicDroppableChildren = (children: TreeNode[], childDepth: number) => (
      <Droppable droppableId={node.id} type="TOPIC">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={snapshot.isDraggingOver ? 'bg-action-blue/10 rounded' : ''}
          >
            {children.map((child, index) => {
              const isLeafTopic = child.type === 'topic' && (!child.children || child.children.length === 0);
              if (!isLeafTopic) {
                return (
                  <div key={child.id}>
                    {renderTreeNode(child, childDepth)}
                  </div>
                );
              }

              return (
                <Draggable key={child.id} draggableId={child.id} index={index}>
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={snapshot.isDragging ? 'opacity-50' : ''}
                    >
                      {renderTreeNode(child, childDepth, dragProvided.dragHandleProps)}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );

    return (
      <div key={node.id}>
        <TreeNodeRenderer
          node={node}
          depth={depth}
          isExpanded={isExpanded}
          isLoading={isLoading}
          showExpandIcon={showExpandIcon}
          showCheckbox={showCheckbox}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          checkboxDisabled={checkboxDisabled}
          isDisabled={isDisabled}
          isSelected={isSelected}
          isTopicSelectedInCards={isTopicSelectedInCards}
          topicLeafInstructionalRollup={leafTopicWithoutChildren ? instructionalRollup : undefined}
          instructionalUnits={leafTopicWithoutChildren ? leafInstructionalUnits : undefined}
          onOpenInstructionalUnit={onOpenInstructionalUnit}
          assessmentScore={assessmentScore}
          passFailThreshold={passFailThreshold}
          hasChildTopics={hasChildTopics}
          childTopicsCount={childTopicsCount}
          yearTopicsCount={yearTopicsCount}
          parentTopicNames={parentTopicNames}
          dragHandleProps={dragHandleProps}
          previousParentTopicNames={undefined}
          previousPrimaryTag={null}
          currentColorIndex={0}
          onToggleExpand={handleToggleOrSelect}
          onCheckboxClick={(e) => handleCheckboxClick(node, e)}
          onNodeClick={handleToggleOrSelect}
        />
        
        {isExpanded && hasChildren && (
          <>
            {node.type === 'year' ? (
              <Droppable droppableId={node.id} type="SEGMENT">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={snapshot.isDraggingOver ? 'bg-action-blue/10 rounded' : ''}
                  >
                    {node.children!.map((child, index) => {
                      const isSegmentLeaf =
                        child.type === 'topic' &&
                        (!child.children || child.children.length === 0);
                      const isNestedParent =
                        child.type === 'topic' &&
                        child.children &&
                        child.children.length > 0;
                      const canDragSegment =
                        child.type === 'topicGroup' || isSegmentLeaf || isNestedParent;

                      if (!canDragSegment) {
                        return (
                          <div key={child.id}>
                            {renderTreeNode(child, depth + 1)}
                          </div>
                        );
                      }

                      return (
                        <Draggable
                          key={child.id}
                          draggableId={child.id}
                          index={index}
                        >
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={snapshot.isDragging ? 'opacity-50' : ''}
                            >
                              {renderTreeNode(
                                child,
                                depth + 1,
                                dragProvided.dragHandleProps
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ) : node.type === 'topicGroup' ? (
              renderTopicDroppableChildren(node.children!, depth + 1)
            ) : (
              <div>
                {node.children!.map(child => renderTreeNode(child, depth + 1))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const treeNodes = buildTreeNodes();

  switch (viewState) {
    case viewStates.loading:
      return (
        <div className={`${className} flex flex-col h-full`}>
          <div className="pl-2 pr-2 lg:pr-4 pb-1 pt-2 flex-shrink-0">
            <Label className="block text-lg font-semibold text-[#1a5172]">
              {studentCurriculumLabel}
            </Label>
          </div>
          <div className="flex flex-1 items-center justify-center px-4 py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue" aria-hidden />
            <span className="ml-2 text-gray-600">Loading curriculum...</span>
          </div>
        </div>
      );

    case viewStates.error:
      return (
        <div className={`${className} flex flex-col h-full`}>
          <div className="pl-2 pr-2 lg:pr-4 pb-1 pt-2 flex-shrink-0">
            <Label className="block text-lg font-semibold text-[#1a5172]">
              {studentCurriculumLabel}
            </Label>
          </div>
          <div className="flex flex-1 items-center justify-center px-4 py-8">
            <div className="text-destructive-action">Error loading curriculum</div>
          </div>
        </div>
      );

    case viewStates.empty:
      return (
        <div className={`${className} flex flex-col h-full`}>
          <div className="pl-2 pr-2 lg:pr-4 pb-1 pt-2 flex-shrink-0">
            <Label className="block text-lg font-semibold text-[#1a5172]">
              {studentCurriculumLabel}
            </Label>
          </div>
          <div className="flex-1 overflow-auto relative">
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <BookOpen className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <p className="text-sm text-gray-500 max-w-sm">
                <Link
                  to="/app/lessons/add-topics"
                  className="font-medium text-action-blue hover:text-action-blue-dark"
                >
                  Open Add Topics
                </Link>
                <br />
                {`to add topics to ${studentFirstName}'s Curriculum.`}
              </p>
            </div>
          </div>
        </div>
      );

    case viewStates.ready:
      return (
        <div className={`${className} flex flex-col h-full`}>
          <div className="flex-1 overflow-auto relative">
            <div className="pl-2 pr-2 lg:pr-4 pb-2 pt-2 flex items-start justify-between gap-2">
              <Label className="block min-w-0 flex-1 pr-2 pb-1 text-lg font-semibold text-[#1a5172]">
                {studentCurriculumLabel}
              </Label>
              <button
                onClick={handleCollapseAll}
                className="shrink-0 text-gray-600 hover:text-gray-900 transition-colors p-1 rounded hover:bg-gray-100 pt-1"
                title={collapseAllTitle}
              >
                <ChevronsUp className="w-4 h-4" />
              </button>
            </div>
            
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="pt-2">
                {treeNodes.map(node => renderTreeNode(node, 0))}
              </div>
            </DragDropContext>
          </div>
        </div>
      );

    default:
      return null;
  }
}
